// File: src/Subprogram/NewsAndReviews.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageCircle, Newspaper, Send, User, 
  Calendar, Clock, Loader2, AlertCircle, CheckCircle 
} from 'lucide-react';

const API_BASE = 'https://free3dmax.ir/api';

const NewsAndReviews = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('news'); // 'news' or 'comments'
  const [news, setNews] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // استیت‌های فرم ثبت نظر
  const [commentText, setCommentText] = useState('');
  const [guestName, setGuestName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  // --- Fetch Data ---
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'news') {
        const res = await axios.get(`${API_BASE}/news.php`);
        // اگر آرایه نبود یا خالی بود هندل شود
        setNews(Array.isArray(res.data) ? res.data : []);
      } else {
        // دریافت نظرات تایید شده (approved=1 پیش‌فرض php است)
        const res = await axios.get(`${API_BASE}/comments.php`);
        setComments(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      setError('خطا در دریافت اطلاعات از سرور.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Handle Submit Comment ---
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    // اعتبارسنجی ساده
    if (!commentText.trim()) return;
    const nameToSend = currentUser ? currentUser.full_name : guestName;
    if (!nameToSend.trim()) {
        setSubmitMsg({ type: 'error', text: 'لطفاً نام خود را وارد کنید.' });
        return;
    }

    setSubmitting(true);
    setSubmitMsg(null);

    try {
      await axios.post(`${API_BASE}/comments.php`, {
        user_id: currentUser ? currentUser.id : 0,
        name: nameToSend,
        text: commentText
      });

      setSubmitMsg({ type: 'success', text: 'نظر شما ثبت شد و پس از تایید نمایش داده می‌شود.' });
      setCommentText('');
      setGuestName('');
    } catch (err) {
      setSubmitMsg({ type: 'error', text: 'خطا در ثبت نظر. دوباره تلاش کنید.' });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Helper: Format Date (Optional) ---
  const formatDate = (dateString) => {
    // تبدیل ساده تاریخ میلادی دیتابیس به شمسی یا فرمت خوانا
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  return (
    <div className="container mx-auto px-4 py-8 pt-36 animate-fade-in-up min-h-screen">
      
      {/* Header & Tabs */}
      <div className="flex flex-col items-center mb-10">
        <h2 className="text-3xl font-black text-slate-800 mb-6">اخبار و گفتگو</h2>
        
        <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200 inline-flex">
          <button
            onClick={() => setActiveTab('news')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${
              activeTab === 'news' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <Newspaper size={18} />
            اخبار سایت
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${
              activeTab === 'comments' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <MessageCircle size={18} />
            نظرات و گفتگو
          </button>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-blue-600" size={40} />
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-10 bg-red-50 rounded-2xl border border-red-100">
           <AlertCircle className="mx-auto mb-2" />
           {error}
        </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          
          {/* --- News Tab --- */}
          {activeTab === 'news' && (
            <div className="space-y-6">
              {news.length === 0 ? (
                <p className="text-center text-slate-400 py-10">خبری موجود نیست.</p>
              ) : (
                news.map((item) => (
                  <div key={item.id} className="bg-white rounded-[2rem] p-8 shadow-sm hover:shadow-md transition-shadow border border-slate-100">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                        <Calendar size={14} />
                        <span>{formatDate(item.created_at)}</span>
                        <span className="mx-2">•</span>
                        <User size={14} />
                        <span>{item.author || 'مدیریت'}</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-4">{item.title}</h3>
                    <p className="text-slate-600 leading-8 whitespace-pre-line text-justify">
                      {item.text}
                    </p>
                  </div>
                ))
              )}
            </div>
          )}

          {/* --- Comments Tab --- */}
          {activeTab === 'comments' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                
                {/* Form Column */}
                <div className="md:col-span-1">
                    <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100 sticky top-32">
                        <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <Send size={18} className="text-blue-500"/>
                            ارسال نظر جدید
                        </h4>
                        
                        <form onSubmit={handleCommentSubmit} className="space-y-4">
                            {!currentUser && (
                                <div>
                                    <label className="text-xs font-bold text-slate-500 mb-1 block">نام شما</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500"
                                        placeholder="نام نمایشی..."
                                        value={guestName}
                                        onChange={e => setGuestName(e.target.value)}
                                    />
                                </div>
                            )}
                            
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1 block">متن پیام</label>
                                <textarea 
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 h-32 resize-none"
                                    placeholder="نظر خود را بنویسید..."
                                    value={commentText}
                                    onChange={e => setCommentText(e.target.value)}
                                ></textarea>
                            </div>

                            <button 
                                disabled={submitting}
                                className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50 flex justify-center"
                            >
                                {submitting ? <Loader2 className="animate-spin" /> : 'ثبت دیدگاه'}
                            </button>

                            {submitMsg && (
                                <div className={`text-xs p-3 rounded-xl flex items-start gap-2 ${submitMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                    {submitMsg.type === 'success' ? <CheckCircle size={14} className="mt-0.5"/> : <AlertCircle size={14} className="mt-0.5"/>}
                                    {submitMsg.text}
                                </div>
                            )}
                        </form>
                    </div>
                </div>

                {/* List Column */}
                <div className="md:col-span-2 space-y-4">
                    {comments.length === 0 ? (
                        <div className="text-center text-slate-400 py-10 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                            هنوز نظری ثبت نشده است. اولین نفر باشید!
                        </div>
                    ) : (
                        comments.map((comment) => (
                            <div key={comment.id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex gap-4">
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 font-bold">
                                        {comment.name.charAt(0)}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-bold text-slate-800">{comment.name}</span>
                                        <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
                                            {formatDate(comment.created_at)}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-6">
                                        {comment.text}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                </div>

            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsAndReviews;